/**
 * Works for both RichTextEditor and JsonRTE
 * 
 * @param {Object} params - Parameters object
 * @param {Object} params.editor - The Slate editor instance
 * @param {Object} params.selectedAsset - The selected asset object with properties: url, uid, description, title, etc.
 * @param {string} [params.type] - Type of insertion: 'asset' for embedded asset, 'assetLink' for asset as link, undefined or 'image' for regular image
 * @param {boolean} [params.isInline] - Whether to insert as inline asset (JsonRTE specific, ignored for RichTextEditor)
 * @param {Function} params.closeModal - Function to close the modal
 * @param {boolean} [params.isJsonRTE] - Explicitly specify if using JsonRTE (recommended). If not provided, will auto-detect via editor.rteVersion
 * @param {Object} [params.linkSettings] - Link settings for assetLink type: { displayText, title, openInNewTab }
 * 
 * @example
 * // Recommended: Explicitly specify editor type
 * import { handleRTEAssetSubmit } from '@contentstack/venus-components'
 * 
 * const handleSelect = () => {
 *   handleRTEAssetSubmit({
 *     editor,
 *     selectedAsset: selectedAsset,
 *     type: 'asset',
 *     isInline: false,
 *     isJsonRTE: true,  // Explicitly specify - cleaner than auto-detection
 *     closeModal: closeModal
 *   })
 * }
 * 
 * // For embedding asset as link:
 * handleRTEAssetSubmit({
 *   editor,
 *   selectedAsset: selectedAsset,
 *   type: 'assetLink',
 *   isJsonRTE: true,
 *   linkSettings: { displayText: 'Click here', title: 'Asset title', openInNewTab: true },
 *   closeModal: closeModal
 * })
 */

// Import both insertEmbedAsset functions
import { insertEmbedAsset as insertRTEEmbedAsset } from '../components/RichTextEditor/SuperChargedRte/elements/Element/contentstack/image/Modal/ModalComponent'
import { insertEmbedAsset as insertJsonRTEEmbedAsset, insertReferenceLink as insertJsonRTEReferenceLink } from '../components/JsonRTE/SuperChargedRte/elements/Element/contentstack/image/Modal/ModalComponent'
import { insertImage as insertRTEImage } from '../components/RichTextEditor/SuperChargedRte/elements/Element/image/utils'
import { insertImage as insertJsonRTEImage } from '../components/JsonRTE/SuperChargedRte/elements/Element/image/utils'

interface LinkSettings {
  displayText?: string;
  title?: string;
  openInNewTab?: boolean;
}

interface HandleRTEAssetSubmitParams {
  editor: any;
  selectedAsset: any;
  type?: string;
  isInline?: boolean;
  isJsonRTE?: boolean;
  closeModal?: () => void;
  linkSettings?: LinkSettings;
}

export const handleRTEAssetSubmit = ({ editor, selectedAsset, type, isInline = false, isJsonRTE, closeModal, linkSettings }: HandleRTEAssetSubmitParams) => {
  const url = selectedAsset?.url;
  const asset_uid = selectedAsset?.uid;
  
  if (!url) {
    return;
  }
  
  // Use explicit isJsonRTE parameter if provided, otherwise fall back to auto-detection
  const useJsonRTE = isJsonRTE !== undefined ? isJsonRTE : (editor?.rteVersion !== undefined);
  
  if (type === "asset") {
    if (useJsonRTE) {
      // JsonRTE: supports isInline parameter
      insertJsonRTEEmbedAsset({ editor, asset: selectedAsset, isInline });
    } else {
      // RichTextEditor: doesn't support isInline parameter
      insertRTEEmbedAsset({ editor, asset: selectedAsset });
    }
  } else if (type === "assetLink") {
    if (useJsonRTE) {
      const displayText = linkSettings?.displayText;
      const title = linkSettings?.title;
      const openInNewTab = linkSettings?.openInNewTab;
      insertJsonRTEReferenceLink({
        editor,
        image: selectedAsset,
        linkTitle: displayText || selectedAsset?.title || url,
        newTab: openInNewTab || false,
        title: title || ""
      });
    }
  } else {
    // For images, use the appropriate insertImage function based on editor type
    if (useJsonRTE) {
      insertJsonRTEImage(editor, url, {
        asset_uid,
        alt: selectedAsset?.description || selectedAsset?.title
      });
    } else {
      insertRTEImage(editor, url, {
        asset_uid,
        alt: selectedAsset?.description || selectedAsset?.title
      });
    }
  }
  
  if (closeModal) {
    closeModal();
  }
}

